import React, { useState } from "react";
import { RIGHT_CLICK_MENU_CONTROLLER } from "../RightClickMenu/RightClickMenu";

const FlowSelector = ({ 
  flows, 
  activeFlowId, 
  onFlowChange, 
  onCreateFlow,
  onRenameFlow,
  onDeleteFlow,
  canCreateFlow = true,
}) => {
  const [dialogMode, setDialogMode] = useState(null);
  const [pendingFlow, setPendingFlow] = useState(null);
  const [flowNameDraft, setFlowNameDraft] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canDeleteFlow = flows.length > 1;

  const closeDialog = () => {
    if (isSubmitting) {
      return;
    }

    setDialogMode(null);
    setPendingFlow(null);
    setFlowNameDraft('');
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setPendingFlow(null);
    setFlowNameDraft('');
  };

  const openRenameDialog = (flow) => {
    setDialogMode('rename');
    setPendingFlow(flow);
    setFlowNameDraft(flow?.name || '');
  };

  const openDeleteDialog = (flow) => {
    setDialogMode('delete');
    setPendingFlow(flow);
    setFlowNameDraft('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = flowNameDraft.trim();
    if (!trimmedName) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (dialogMode === 'create') {
        const created = await onCreateFlow?.(trimmedName);
        if (created !== false) {
          closeDialog();
        }
      } else if (dialogMode === 'rename' && pendingFlow?.id) {
        const renamed = await onRenameFlow?.(pendingFlow.id, trimmedName);
        if (renamed !== false) {
          closeDialog();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingFlow?.id || !onDeleteFlow) {
      return;
    }

    setIsSubmitting(true);

    try {
      const deleted = await onDeleteFlow(pendingFlow.id);
      if (deleted !== false) {
        closeDialog();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFlowFeatureSummary = (flow) => {
    const featureCounts = new Map();

    (flow?.steps || []).forEach((step) => {
      (step?.components || []).forEach((component) => {
        const label = (component?.label || component?.customName || 'Untitled feature').trim();
        featureCounts.set(label, (featureCounts.get(label) || 0) + 1);
      });
    });

    return Array.from(featureCounts.entries()).map(([label, count]) => (
      count > 1 ? `${label} x${count}` : label
    ));
  };

  const handleFlowContextMenu = (event, flow) => {
    const menuItems = [
      {
        label: 'Rename Flow',
        action: () => {
          RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
          openRenameDialog(flow);
        },
      },
      {
        label: 'Delete Flow',
        disabled: !canDeleteFlow,
        action: !canDeleteFlow ? null : () => {
          RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
          openDeleteDialog(flow);
        },
      },
    ];

    RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(event, menuItems);
  };

  const flowFeatures = pendingFlow ? getFlowFeatureSummary(pendingFlow) : [];
  const showNameDialog = dialogMode === 'create' || dialogMode === 'rename';

  return (
    <div className="flow-selector">
      <div className="flow-selector-header">
        <span className="flow-label">Flows</span>
        {canCreateFlow && (
          <button 
            className="flow-add-btn" 
            onClick={handleCreateClick}
            title="Create new flow"
          >
            +
          </button>
        )}
      </div>
      
      <div className="flow-pills">
        {flows.map(flow => (
          <button
            type="button"
            key={flow.id}
            className={`flow-pill ${activeFlowId === flow.id ? 'active' : ''}`}
            onClick={() => onFlowChange(flow.id)}
            onContextMenu={(event) => handleFlowContextMenu(event, flow)}
          >
            {flow.name}
          </button>
        ))}
      </div>

      {showNameDialog && (
        <div className="flow-modal-overlay" onClick={closeDialog}>
          <div className="flow-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{dialogMode === 'rename' ? 'Rename Flow' : 'Enter Flow Name'}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                value={flowNameDraft}
                onChange={(e) => setFlowNameDraft(e.target.value)}
                placeholder="Flow name..."
                autoFocus
                maxLength={30}
              />
              <div className="flow-modal-buttons">
                <button className="standard-button" type="button" onClick={closeDialog}>
                  Cancel
                </button>
                <button className={`standard-button ${isSubmitting || !flowNameDraft.trim() ? 'button-inactive' : 'button-active'}`} type="submit" disabled={isSubmitting || !flowNameDraft.trim()}>
                  {dialogMode === 'rename' ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dialogMode === 'delete' && pendingFlow && (
        <div className="flow-modal-overlay" onClick={closeDialog}>
          <div className="flow-modal feature-delete-modal flow-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Flow?</h3>
            <p className="feature-delete-modal-copy">
              Are you sure you want to delete <strong>{pendingFlow.name}</strong>?
            </p>
            <p className="feature-delete-modal-copy feature-delete-modal-copy--secondary">
              This flow contains the following features:
            </p>
            {flowFeatures.length > 0 ? (
              <ul className="flow-delete-feature-list">
                {flowFeatures.map((featureLabel) => (
                  <li key={featureLabel}>{featureLabel}</li>
                ))}
              </ul>
            ) : (
              <p className="flow-delete-empty-state">No features are currently in this flow.</p>
            )}
            <div className="flow-modal-buttons">
              <button className="standard-button" type="button" onClick={closeDialog}>
                Cancel
              </button>
              <button
                type="button"
                className={`standard-button red-button ${isSubmitting || !canDeleteFlow ? 'button-inactive' : 'button-active'}`}
                onClick={handleDeleteConfirm}
                disabled={isSubmitting || !canDeleteFlow}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Flow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowSelector;
